
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type DepthShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uModelMatrix: WebGLUniformLocation,
	uBindShapeMatrix: WebGLUniformLocation,
	uMatrixSampler: WebGLUniformLocation,
	uDataSettings: WebGLUniformLocation,
	uBlendSettings: WebGLUniformLocation,
	uBoneOverrides: WebGLUniformLocation[],
	uBoneOverrideIndices: WebGLUniformLocation[],
	uBoneOverrideCount: WebGLUniformLocation,
	uBoneMatrix: WebGLUniformLocation,
	uDepthPoint: WebGLUniformLocation,
	uDitherFade: WebGLUniformLocation
};

export type DepthShaderAttributes = {
	aInstanceModelCol0: number,
	aInstanceModelCol1: number,
	aInstanceModelCol2: number,
	aInstanceModelCol3: number,
	aVertexPosition: number,
	aJointWeights: number,
	aJointIndices: number,
	vWorldPosition: number,
	vPosition: number
};

export type DepthShaderSettings = ('INSTANCED' | 'ANIMATION' | 'BONE_OVERRIDES' | 'ATTACHMENT' | 'CROSSFADE')[];

export class DepthShader extends ShaderBase<DepthShaderUniforms, DepthShaderAttributes> {

    private static fragmentShaderSource = `
in vec4 vWorldPosition;
in vec4 vPosition;
out vec4 fragColor;

// uDepthPoint.w == 0: spot/directional light (hardware depth buffer used, fragColor is dummy)
// uDepthPoint.w != 0: point light (output linear distance to light as float)
uniform vec4 uDepthPoint;

#ifdef NOODLE_CROSSFADE
uniform float uDitherFade;
const float bayer4x4[16] = float[](
	 0.0/16.0,  8.0/16.0,  2.0/16.0, 10.0/16.0,
	12.0/16.0,  4.0/16.0, 14.0/16.0,  6.0/16.0,
	 3.0/16.0, 11.0/16.0,  1.0/16.0,  9.0/16.0,
	15.0/16.0,  7.0/16.0, 13.0/16.0,  5.0/16.0
);
#endif

void main(void) {
#ifdef NOODLE_CROSSFADE
	ivec2 pc = ivec2(gl_FragCoord.xy);
	float ditherThreshold = bayer4x4[(pc.x & 3) * 4 + (pc.y & 3)];
	if (uDitherFade >= 0.0) {
		if (ditherThreshold < uDitherFade) discard;
	} else {
		if (ditherThreshold >= -uDitherFade) discard;
	}
#endif
	if (uDepthPoint.w == 0.0) {
		// Spot/directional: hardware depth buffer captures the depth automatically.
		// Write dummy value to satisfy color attachment requirement.
		fragColor = vec4(0.0, 0.0, 0.0, 1.0);
	} else {
		// Point light: store linear distance to light source.
		// WebGL2 supports float textures natively, no encoding needed.
		vec3 direction = vWorldPosition.xyz - uDepthPoint.xyz;
		fragColor = vec4(length(direction), 0.0, 0.0, 1.0);
	}
}
`;

    private static vertexShaderSource = `
uniform mat4 uProjectionMatrix;

#ifdef NOODLE_INSTANCED
layout(location = 6) in vec4 aInstanceModelCol0;
layout(location = 7) in vec4 aInstanceModelCol1;
layout(location = 8) in vec4 aInstanceModelCol2;
layout(location = 9) in vec4 aInstanceModelCol3;
uniform mat4 uViewMatrix;
#else
uniform mat4 uModelViewMatrix;
uniform mat4 uModelMatrix;
#endif

layout(location = 0) in vec3 aVertexPosition;
out vec4 vWorldPosition;
out vec4 vPosition;

#ifdef NOODLE_ANIMATION
// --- begin: animation.glsl ---
layout(location = 4) in vec4 aJointWeights;
layout(location = 5) in vec4 aJointIndices;

uniform mat4 uBindShapeMatrix;
uniform sampler2D uMatrixSampler;
uniform vec3 uDataSettings;
uniform vec4 uBlendSettings;

#ifdef NOODLE_BONE_OVERRIDES
uniform mat4 uBoneOverrides[32];
uniform int uBoneOverrideIndices[32];
uniform int uBoneOverrideCount;
#endif

mat4 read_matrix(sampler2D tex, float i, vec2 size) {
	const float offset = 0.5;
	float tx = mod(i * 4.0, size.x) + offset;
	float ty = floor(i * 4.0 / size.x) + offset;

	return mat4(
		texture(tex, vec2((tx + 0.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 1.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 2.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 3.0) / size.x, ty / size.y))
	);
}

mat4 read_animation() {
	mat4 matrix = mat4(0.0);
	for (int j = 0; j < 4; j++) {
		float w = aJointWeights[j];
		if (w > 0.0) {
			float jointIndex = float(aJointIndices[j]);
			float index = jointIndex + uDataSettings.z;
			mat4 jointMatrix = read_matrix(uMatrixSampler, index, uDataSettings.xy);

			// Blend with previous clip if active
			if (uBlendSettings.w > 0.0) {
				mat4 prevMatrix = read_matrix(uMatrixSampler, jointIndex + uBlendSettings.z, uBlendSettings.xy);
				jointMatrix = prevMatrix + (jointMatrix - prevMatrix) * uBlendSettings.w;
			}

			#ifdef NOODLE_BONE_OVERRIDES
			for (int k = 0; k < uBoneOverrideCount; k++) {
				if (uBoneOverrideIndices[k] == int(jointIndex)) {
					jointMatrix = uBoneOverrides[k];
					break;
				}
			}
			#endif

			matrix += jointMatrix * w;
		}
	}
	return matrix;
}

// --- end: animation.glsl ---
#endif

#ifdef NOODLE_ATTACHMENT
uniform mat4 uBoneMatrix;
#endif

void main(void) {

#ifdef NOODLE_ANIMATION
	mat4 animationMatrix = read_animation();
	vec4 pos = vec4(aVertexPosition, 1.0) * uBindShapeMatrix * animationMatrix;
#else
	vec4 pos = vec4(aVertexPosition, 1.0);
#endif

#ifdef NOODLE_ATTACHMENT
	pos = pos * uBoneMatrix;
#endif

#ifdef NOODLE_INSTANCED
	mat4 instanceModel = mat4(aInstanceModelCol0, aInstanceModelCol1, aInstanceModelCol2, aInstanceModelCol3);
	vWorldPosition = instanceModel * vec4(pos.xyz, 1.0);
	vPosition = uViewMatrix * vWorldPosition;
	gl_Position = uProjectionMatrix * vPosition;
#else
	vWorldPosition = uModelMatrix * vec4(pos.xyz, 1.0);
	vPosition = uModelViewMatrix * vec4(pos.xyz, 1.0);
	gl_Position = uProjectionMatrix * vPosition;
#endif
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: DepthShaderUniformsContainer;
    public attributesContainer: DepthShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: DepthShaderSettings) {
        super(ctx, DepthShader.fragmentShaderSource, DepthShader.vertexShaderSource, settings);
        this.uniformsContainer = new DepthShaderUniformsContainer(ctx, this);
        this.attributesContainer = new DepthShaderShaderAttributesContainer(ctx, this);
    }
}

class DepthShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: DepthShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setModelMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setBindShapeMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uBindShapeMatrix', this.shader.uniforms.uBindShapeMatrix, value, 'mat4');
        return this;
    }

    public setMatrixSampler(texture: Texture) : DepthShaderUniformsContainer {
        this.setTextureValue('uMatrixSampler', this.shader.uniforms.uMatrixSampler, texture, 'sampler2D');
        return this;
    }

    public setDataSettings(value: vec3 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uDataSettings', this.shader.uniforms.uDataSettings, value, 'vec3');
        return this;
    }

    public setBlendSettings(value: vec4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uBlendSettings', this.shader.uniforms.uBlendSettings, value, 'vec4');
        return this;
    }

    public setBoneOverrides(index: number, value: mat4 | number[]) : DepthShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrides: ' + index);
        this.setValue('uBoneOverrides', this.shader.uniforms.uBoneOverrides[index], value, 'mat4');
        return this;
    }

    public setBoneOverrideIndices(index: number, value: number) : DepthShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrideIndices: ' + index);
        this.setValue('uBoneOverrideIndices', this.shader.uniforms.uBoneOverrideIndices[index], value, 'int');
        return this;
    }

    public setBoneOverrideCount(value: number) : DepthShaderUniformsContainer {
        this.setValue('uBoneOverrideCount', this.shader.uniforms.uBoneOverrideCount, value, 'int');
        return this;
    }

    public setBoneMatrix(value: mat4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uBoneMatrix', this.shader.uniforms.uBoneMatrix, value, 'mat4');
        return this;
    }

    public setDepthPoint(value: vec4 | number[]) : DepthShaderUniformsContainer {
        this.setValue('uDepthPoint', this.shader.uniforms.uDepthPoint, value, 'vec4');
        return this;
    }

    public setDitherFade(value: number) : DepthShaderUniformsContainer {
        this.setValue('uDitherFade', this.shader.uniforms.uDitherFade, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMatrixSampler', uniform: this.shader.uniforms.uMatrixSampler, dataType: 'sampler2D' }
        ];
    }
}

class DepthShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: DepthShader) {
        super(ctx);
    }

    public setInstanceModelCol0(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol0, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol1(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol1, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol2(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol2, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol3(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol3, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointWeights(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointIndices(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointIndices, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvPosition(settings: { length: number, offset: number, itemSize: number }) : DepthShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}